import {
	Event,
	Leaderboard,
	LeaderboardTeam,
	ResultWithRider,
	Series,
	SnapshotWithEventandRider,
	Sport,
	TeamResultRiderWithRider,
	TeamResultWithRider,
	User,
} from "../types/model";

export function filterEventsBySportAndSeries(
	events: Event[],
	series?: Series,
	sport?: Sport,
): Event[] {
	return events.filter((event: Event) => {
		const matchesSeries = series ? event.series._id === series._id : true;
		const matchesSport = sport ? event.sport._id === sport._id : true;
		return matchesSeries && matchesSport;
	});
}

export function getFutureEvents(events: Event[]): Event[] {
	return events
		.filter((event: Event) => {
			return (
				new Date(event.date) > new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
			);
		})
		.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

export function getSeasonEvents(events: Event[], seasonId: string): Event[] {
	return seasonId
		? events.filter((event: Event) => event.season === seasonId)
		: events;
}

export function getNearestEvent(events: Event[]): Event | null {
	const futureEvents = getFutureEvents(events);
	futureEvents.sort(
		(a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
	);
	return futureEvents.length > 0 ? futureEvents[0] : null;
}

export function getEventSeries(events: Event[]): Series[] {
	return events.reduce((acc: Series[], event: Event) => {
		if (!acc.some((series: Series) => series._id === event.series._id)) {
			acc.push(event.series);
		}
		return acc;
	}, []);
}

export function getSeriesByEventsAndSport(
	events: Event[],
	sport: Sport,
): Series[] {
	return events
		.filter((event: Event) => event.sport._id === sport._id)
		.reduce((acc: Series[], event: Event) => {
			if (!acc.some((series: Series) => series._id === event.series._id)) {
				acc.push(event.series);
			}
			return acc;
		}, []);
}

export function getEventSports(events: Event[]): Sport[] {
	return events.reduce((acc: Sport[], event: Event) => {
		if (!acc.some((sport: Sport) => sport._id === event.sport._id)) {
			acc.push(event.sport);
		}
		return acc;
	}, []);
}

export function getSportsByEventsAndSeries(
	events: Event[],
	series: Series,
): Sport[] {
	return events
		.filter((event: Event) => event.series._id === series._id)
		.reduce((acc: Sport[], event: Event) => {
			if (!acc.some((sport: Sport) => sport._id === event.sport._id)) {
				acc.push(event.sport);
			}
			return acc;
		}, []);
}

export const getPositionInLeague = (
	leagueId: string,
	teamLeaderboards: Leaderboard[],
	user: User,
) => {
	const leaderboard = teamLeaderboards.find(
		(leaderboard) => leaderboard.leagueId === leagueId,
	);

	if (leaderboard) {
		const userPosition =
			[...(leaderboard.teams ?? [])]
				.sort(
					(a: LeaderboardTeam, b: LeaderboardTeam) =>
						b.totalPoints - a.totalPoints,
				)
				.findIndex((team: LeaderboardTeam) => team.userId === user._id) + 1;

		return userPosition > 0 ? userPosition : 0;
	}
	return 0;
};

export const getTeamPointsFromLeaderboard = (
	leaderboard: Leaderboard,
	teamId: string,
): number => {
	const team = leaderboard.teams?.find(
		(team: LeaderboardTeam) => team.teamId === teamId,
	);
	return team ? team.totalPoints : 0;
};

export const getTeamResultForSnapshot = (
	snapshot: SnapshotWithEventandRider,
	teamResults: TeamResultWithRider[],
) => {
	return teamResults.find(
		(teamResult) =>
			teamResult.event === snapshot.event._id &&
			teamResult.brandTeamId == snapshot.selectedTeam,
	);
};

// Get the sum of rider points for the team and event
export const getTotalPoints = (
	snapshot: SnapshotWithEventandRider,
	results: ResultWithRider[],
) => {
	return results
		.filter(
			(result) =>
				result.event === snapshot.event._id &&
				snapshot.riders.some((rider) => rider.rider._id === result.rider._id),
		)
		.reduce((acc, result) => {
			return (
				acc +
				(result?.qualifying_data?.points ?? 0) +
				(result?.semis_data?.points ?? 0) +
				(result?.finals_data?.points ?? 0)
			);
		}, 0);
};

export const getTotalPointsForTeam = (
	snapshot: SnapshotWithEventandRider,
	teamResults: TeamResultWithRider[],
	results: ResultWithRider[],
) => {
	const teamResult = getTeamResultForSnapshot(snapshot, teamResults);
	return getTotalPoints(snapshot, results) + (teamResult?.points ?? 0);
};

declare global {
	interface Window {
		_klOnsite?: any;
	}
}

export function openKlaviyoForm(formId: string) {
	window._klOnsite = window._klOnsite || [];
	if (typeof window._klOnsite.openForm === "function") {
		window._klOnsite.openForm([formId]);
	} else {
		window._klOnsite.push(["openForm", formId]);
	}
}
